import { menuCategories } from "@/data/menu";
import { IMAGES } from "@/lib/config";
import { useLang, pick } from "@/lib/i18n";
import { menuSchema, breadcrumbSchema } from "@/lib/schema";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { OrderButton } from "@/components/TrackedLinks";

export const MenuSections = ({ showOrder = true }) => {
  const { lang } = useLang();
  return (
    <div className="space-y-6">
      <nav aria-label="Menu categories" className="flex flex-wrap gap-2 justify-center">
        {menuCategories.map((cat) => (
          <a key={cat.key} href={`#menu-${cat.key}`} className="px-4 py-2 rounded-full border-2 border-hacienda-border-light text-sm font-medium text-hacienda-charcoal hover:border-hacienda-terracotta hover:text-hacienda-terracotta transition-colors" data-testid={`menu-jump-${cat.key}`}>
            {pick(cat.title, lang)}
          </a>
        ))}
      </nav>
      {menuCategories.map((cat) => (
        <section key={cat.key} id={`menu-${cat.key}`} className="bg-hacienda-cream rounded-2xl p-6 sm:p-8 scroll-mt-28" data-testid={`menu-section-${cat.key}`}>
          <div className="flex items-center gap-3 mb-2">
            <cat.icon size={26} className="text-hacienda-terracotta" aria-hidden="true" />
            <h3 className="font-serif text-3xl text-hacienda-charcoal font-semibold">
              {pick(cat.title, lang)} <span className="text-hacienda-gray text-xl font-normal">/ {cat.title[lang === "en" ? "es" : "en"]}</span>
            </h3>
          </div>
          {cat.note && <p className="text-hacienda-gray text-sm mb-6 italic">{pick(cat.note, lang)}</p>}
          <ul className="grid md:grid-cols-2 gap-4 mt-4">
            {cat.items.map((item, i) => (
              <li key={item.name} className="bg-white p-5 rounded-xl border border-hacienda-border-light menu-card flex flex-col" data-testid={`menu-item-${cat.key}-${i}`}>
                <div className="flex justify-between items-start gap-3 mb-1">
                  <h4 className="font-semibold text-hacienda-charcoal text-lg">{item.name}</h4>
                  <span className="text-hacienda-terracotta font-bold whitespace-nowrap">{item.price}</span>
                </div>
                {item.description && <p className="text-hacienda-gray text-sm flex-grow">{item.description}</p>}
                {showOrder && <OrderButton location={`menu_${cat.key}`} variant="ghost" size="sm" className="!px-0 self-start mt-2 text-sm" />}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export const MenuPage = () => {
  const { t, lang } = useLang();
  return (
    <div data-testid="menu-page">
      <Seo
        title="Birria Menu & Online Ordering | Tijuas Tacos Albuquerque"
        description="See the full Tijuas Tacos Birrieria menu: quesabirria tacos, tacos dorados, birria ramen, birria pizza, birria fries and more. Tijuana-style birria de res in Albuquerque—order online for pickup or delivery."
        path="/menu"
        jsonLd={[menuSchema(menuCategories, lang), breadcrumbSchema([{ label: t("nav.menu"), to: "/menu" }])]}
      />
      <Breadcrumbs items={[{ label: t("nav.menu"), to: "/menu" }]} />

      <section className="py-16 px-4" data-testid="menu-hero">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-hacienda-terracotta text-sm uppercase tracking-[0.2em] mb-4">{t("menu.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl text-hacienda-charcoal font-semibold mb-6">{t("menu.title")}</h1>
          <p className="text-hacienda-gray text-lg max-w-2xl mx-auto mb-8">{t("menu.sub")}</p>
          <OrderButton location="menu_hero" size="lg" data-testid="menu-hero-order-btn" />
        </div>
      </section>

      <section className="py-16 px-4 bg-hacienda-charcoal" data-testid="menu-image-section">
        <div className="max-w-6xl mx-auto">
          <img src={IMAGES.menuBoard} alt="Tijuas Tacos Birrieria printed menu board with all plates, specialties, a la carte items, kids meals, drinks and extras" loading="lazy" decoding="async" width="1920" height="1080" className="w-full rounded-2xl shadow-2xl" data-testid="menu-full-image" />
          <p className="text-center text-hacienda-cream/60 mt-4 text-sm">{t("menu.priceNote")} © 2026 Tijuas Tacos LLC.</p>
        </div>
      </section>

      <section className="py-20 px-4" data-testid="menu-details-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-4xl text-hacienda-charcoal font-semibold mb-3">{t("menu.details")}</h2>
            <p className="text-hacienda-gray">{t("menu.detailsSub")}</p>
          </div>
          <MenuSections />
        </div>
      </section>

      <section className="py-16 px-4 bg-hacienda-terracotta" data-testid="menu-order-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl text-white font-semibold mb-4">{t("menu.readyTitle")}</h2>
          <p className="text-white/90 text-lg mb-8">{t("menu.readySub")}</p>
          <OrderButton location="menu_bottom" variant="light" size="lg" data-testid="menu-bottom-order-btn" />
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
